import { useState, useEffect, useRef, useMemo } from "react";
import { Search, X, Package, Check, RefreshCw, ArrowRight } from "lucide-react";
import { resolveImageUrl } from "../../utils/images";
import { formatMoney } from "../../utils/format";
import "./ProductSearchPicker.css";

export default function ProductSearchPicker({
  products = [],
  selectedProduct = null,
  onSelectProduct,
  onClearProduct,
  loading = false,
  autoFocus = true,
}) {
  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef(null);
  const resultsRef = useRef(null);

  // Filter products dynamically on every character
  const filteredProducts = useMemo(() => {
    if (!query.trim()) return products;
    const q = query.toLowerCase().trim();
    return products.filter((p) => {
      const name = (p.name || "").toLowerCase();
      const arabicName = (p.arabicName || "").toLowerCase();
      const code = (p.code || "").toLowerCase();
      const id = (p.id || "").toLowerCase();
      return name.includes(q) || arabicName.includes(q) || code.includes(q) || id.includes(q);
    });
  }, [products, query]);

  // Reset highlight index when query changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [query]);

  // Scroll active item into view during keyboard navigation
  useEffect(() => {
    if (highlightedIndex >= 0 && resultsRef.current) {
      const itemEl = resultsRef.current.children[highlightedIndex];
      if (itemEl) {
        itemEl.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex]);

  const handleKeyDown = (e) => {
    if (filteredProducts.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev < filteredProducts.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filteredProducts.length - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && filteredProducts[highlightedIndex]) {
        handleSelect(filteredProducts[highlightedIndex]);
      }
    }
  };

  const handleSelect = (product) => {
    onSelectProduct(product);
  };

  const handleClear = () => {
    onClearProduct();
    setQuery("");
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  // If a product is currently selected, show the pinned selected banner
  if (selectedProduct) {
    return (
      <div className="product-selected-banner">
        <div className="product-selected-banner__main">
          <div
            className="product-selected-banner__thumb"
            style={{
              backgroundImage: selectedProduct.thumbnailImage
                ? `url(${resolveImageUrl(selectedProduct.thumbnailImage)})`
                : undefined,
            }}
          >
            {!selectedProduct.thumbnailImage && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "var(--text-muted)" }}>
                <Package size={24} />
              </div>
            )}
          </div>
          <div className="product-selected-banner__details">
            <h4 className="product-selected-banner__title">
              {selectedProduct.name} {selectedProduct.arabicName ? `(${selectedProduct.arabicName})` : ""}
            </h4>
            <div className="product-selected-banner__badges">
              <code className="product-picker-row__sku">
                {selectedProduct.code || "NO SKU"}
              </code>
              {selectedProduct.basePrice != null && (
                <span className="product-picker-row__price">
                  {formatMoney(selectedProduct.basePrice)}
                </span>
              )}
              <span className="product-picker-row__id">ID: {selectedProduct.id}</span>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={handleClear}
          className="product-selected-banner__btn-change"
          title="Return to product list"
        >
          <RefreshCw size={14} /> Change Product
        </button>
      </div>
    );
  }

  return (
    <div className="product-search-picker-panel">
      {/* 1. Fixed / Pinned Search Input at the Top */}
      <div className="product-search-picker-panel__header">
        <div className="product-search-picker-panel__input-row">
          <Search size={18} className="product-search-picker-panel__search-icon" />
          <input
            ref={inputRef}
            type="text"
            className="product-search-picker-panel__input"
            placeholder={loading ? "Loading product catalog..." : "Type to filter by product name, SKU / code, or ID..."}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus={autoFocus}
            disabled={loading}
          />
          {query && (
            <button
              type="button"
              className="product-search-picker-panel__clear-btn"
              onClick={() => setQuery("")}
              title="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>

        <div className="product-search-picker-panel__status-row">
          <span>
            {query.trim()
              ? `Filtered: ${filteredProducts.length} of ${products.length} products`
              : `Showing all ${products.length} products (scroll down to browse)`}
          </span>
          <span className="product-search-picker-panel__badge">
            Use ↑↓ to navigate, Enter to select
          </span>
        </div>
      </div>

      {/* 2. Dedicated Scrollable Results List Below */}
      <div className="product-search-picker-panel__results" ref={resultsRef}>
        {loading ? (
          <div className="product-picker-empty">
            <span className="page-loading__dot" style={{ width: "10px", height: "10px" }} />
            <span>Loading product inventory...</span>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="product-picker-empty">
            <Package size={36} />
            <strong style={{ color: "var(--text-primary)" }}>No products found</strong>
            <p style={{ margin: 0, fontSize: "13px" }}>
              No items matching "{query}". Try a different name, SKU, or ID.
            </p>
            <button
              type="button"
              className="btn-outline"
              onClick={() => setQuery("")}
              style={{ padding: "6px 14px", fontSize: "13px", marginTop: "4px" }}
            >
              Clear Search Filter
            </button>
          </div>
        ) : (
          filteredProducts.map((p, index) => {
            const isFocused = index === highlightedIndex;
            return (
              <div
                key={p.id}
                role="button"
                tabIndex={0}
                className={`product-picker-row ${isFocused ? "is-focused" : ""}`}
                onClick={() => handleSelect(p)}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                <div className="product-picker-row__main">
                  <div
                    className="product-picker-row__thumb"
                    style={{
                      backgroundImage: p.thumbnailImage
                        ? `url(${resolveImageUrl(p.thumbnailImage)})`
                        : undefined,
                    }}
                  >
                    {!p.thumbnailImage && <Package size={22} />}
                  </div>

                  <div className="product-picker-row__details">
                    <div className="product-picker-row__name">
                      {p.name} {p.arabicName ? `(${p.arabicName})` : ""}
                    </div>
                    <div className="product-picker-row__meta">
                      <code className="product-picker-row__sku">
                        SKU: {p.code || "None"}
                      </code>
                      {p.basePrice != null && (
                        <span className="product-picker-row__price">
                          {formatMoney(p.basePrice)}
                        </span>
                      )}
                      <span className="product-picker-row__id">
                        ID: {p.id.substring(0, 8)}...
                      </span>
                    </div>
                  </div>
                </div>

                <div className="product-picker-row__action">
                  <button type="button" className="product-picker-row__btn-select">
                    Select <ArrowRight size={13} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
